import React, { useState, useMemo } from "react";
import { Plus, FileEdit } from "lucide-react";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@shared/ui/alert-dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { GenericTable } from "@shared/components/GenericTable";
import { useSopNotes, useDeleteSopNote } from "../../../../hooks";
import { AddNoteModal, EditNoteModal } from "../modals/NotesModal";

interface NotesTabProps {
  sopDetails: any;
}

interface Note {
  code: string;
  name: string;
  isActive: boolean;
  noteColor: string;
  createdDate?: string;
  noteDescription: string;
}

const NotesTab: React.FC<NotesTabProps> = ({ sopDetails }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [selectedNoteCode, setSelectedNoteCode] = useState<string>("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<{
    code: string;
    note: Note;
  } | null>(null);

  const deleteNoteMutation = useDeleteSopNote();

  // Fetch notes data using the hook
  const {
    data: notesData,
    isLoading: notesLoading,
    error: notesError,
  } = useSopNotes(sopDetails.id.toString());

  const handleAddNote = () => {
    setIsAddModalOpen(true);
  };

  const handleEditNote = (noteCode: string, note: Note) => {
    setSelectedNote(note);
    setSelectedNoteCode(noteCode);
    setIsEditModalOpen(true);
  };

  const handleDeleteNote = (noteCode: string, note: Note) => {
    setNoteToDelete({ code: noteCode, note });
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteNote = async () => {
    if (!noteToDelete) return;

    try {
      await deleteNoteMutation.mutateAsync({
        sopId: sopDetails.id.toString(),
        noteCode: noteToDelete.code,
      });

      setDeleteConfirmOpen(false);
      setNoteToDelete(null);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedNote(null);
    setSelectedNoteCode("");
  };

  // Convert notes object to array for table display
  const notesArray: Array<Note & { code: string; index: number }> =
    useMemo(() => {
      if (!notesData?.result) return [];

      return Object.entries(notesData.result)
        .map(([code, note]) => ({
          code,
          ...(note as Note),
        }))
        .filter((note) => note.isActive)
        .map((note, index) => ({
          ...note,
          index,
        }));
    }, [notesData]);

  // Helper functions for rendering
  const getColorClassName = (color: string) => {
    switch (color.toLowerCase()) {
      case "red":
        return "bg-red-100 text-red-800 border-red-200";
      case "blue":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "green":
        return "bg-green-100 text-green-800 border-green-200";
      case "yellow":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "purple":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "orange":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "gray":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-slate-100 text-slate-800 border-slate-200";
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "Invalid date";
    }
  };

  // GenericTable column configuration
  const tableColumns = [
    {
      key: "index",
      header: "Sr. No",
      render: (value: number) => (
        <span className="font-medium">{value + 1}</span>
      ),
    },
    {
      key: "code",
      header: "Code",
      render: (value: string) => (
        <code className="bg-gray-100 px-2 py-1 rounded text-sm">{value}</code>
      ),
    },
    {
      key: "name",
      header: "Note Name",
      sortable: true,
      render: (value: string) => (
        <span className="font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: "noteDescription",
      header: "Description",
      render: (value: string) => (
        <div className="max-w-xs">
          {value ? (
            <p className="text-sm line-clamp-2">{value}</p>
          ) : (
            <span className="text-muted-foreground text-sm">
              No description
            </span>
          )}
        </div>
      ),
    },
    {
      key: "noteColor",
      header: "Color",
      render: (value: string) => (
        <Badge
          variant="outline"
          className={`capitalize ${getColorClassName(value)}`}
        >
          {value}
        </Badge>
      ),
    },
    {
      key: "createdDate",
      header: "Created",
      sortable: true,
      render: (value: string) => (
        <span className="text-sm text-muted-foreground">
          {formatDate(value)}
        </span>
      ),
    },
  ];

  // GenericTable actions configuration
  const tableActions = [
    {
      label: "Edit Note",
      onClick: (row: Note & { code: string }) => handleEditNote(row.code, row),
      icon: "Pencil" as const,
      variant: "ghost" as const,
    },
    {
      label: "Delete Note",
      onClick: (row: Note & { code: string }) =>
        handleDeleteNote(row.code, row),
      icon: "Trash2" as const,
      variant: "ghost" as const,
      className: "text-destructive hover:text-destructive",
    },
  ];

  return (
    <>
      <div className="mt-4 space-y-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center pb-2">
                <FileEdit className="mr-2 h-5 w-5 text-primary" />
                Notes
              </CardTitle>
              <CardDescription>
                Additional notes for this procedure
              </CardDescription>
            </div>
            <Button onClick={handleAddNote}>
              <Plus className="mr-2 h-4 w-4" />
              Add
            </Button>
          </CardHeader>
          <CardContent>
            {notesLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="ml-2 text-muted-foreground">
                  Loading notes...
                </span>
              </div>
            ) : notesError ? (
              <div className="text-center py-8 text-destructive">
                Error loading notes: {notesError.message}
              </div>
            ) : (
              <GenericTable
                data={notesArray}
                columns={tableColumns}
                rowKey="code"
                actions={tableActions}
                actionsType="dropdown"
                emptyMessage="No notes defined for this SOP"
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Note Modal */}
      <AddNoteModal
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
        sopId={sopDetails.id.toString()}
        remoteUtilityId={sopDetails.remoteUtilityId?.toString()}
      />

      {/* Edit Note Modal */}
      <EditNoteModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        sopId={sopDetails.id.toString()}
        note={selectedNote}
        noteCode={selectedNoteCode}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the note "
              {noteToDelete?.note.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteNote}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteNoteMutation.isPending}
            >
              {deleteNoteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default NotesTab;
